import { inject, Injectable } from '@angular/core';
import {AnyHash} from '@hidat/huijs-common';
import {EventTrackerConfig} from "./event-tracker-config";
import {GaActionEnum, GoogleAnalyticsService} from "ngx-google-analytics";

/**
 * Analytics Tracking Service.
 * Used to track custom user interactions across the app, sending them to Google Analytics
 * NOTE: Currently only sends to the primary ga property code
 */
@Injectable()
export class EventTrackerService {
  private primaryTrackingCode: string | undefined;
  private secondaryTrackingCode: string | undefined;
  protected gaService = inject(GoogleAnalyticsService);

  constructor() {
    const config = inject(EventTrackerConfig);
    this.primaryTrackingCode = config.primaryTrackingCode;
    this.secondaryTrackingCode = config.secondaryTrackingCode;
    console.debug('Loaded event tracker with ', config.primaryTrackingCode);
  }

  public trackEvent(eventType: GaActionEnum | string, data: AnyHash) {
    this.gaService.gtag('event', eventType, data);
  }

  public trackEventWithSource(
    eventType: GaActionEnum | string,
    eventSource?: string,
    data?: AnyHash
  ) {
    if (eventSource) {
      data = data ?? {};
      data['event_source'] = eventSource;
    }
    this.gaService.gtag('event', eventType, data);
  }

  public pageView(url: string, title?: string): void {
    if (this.primaryTrackingCode != null) {
      this.gaService.pageView(url, title);
    }
    /*
    if (this.secondaryTrackingCode) {
      this.track('config', this.secondaryTrackingCode, {page_path: url});
    }*/
  }

  public playResumed(eventSource: string): void {
    this.trackEventWithSource('player_resume', eventSource);
  }
  public playPaused(eventSource: string): void {
    this.trackEventWithSource('player_pause', eventSource);
  }
  public playSkipForward(eventSource: string): void {
    this.trackEventWithSource('skip_forward', eventSource);
  }
  public playSkipBackward(eventSource: string): void {
    this.trackEventWithSource('skip_backward', eventSource);
  }
  public playSkipNextSegment(eventSource: string): void {
    this.trackEventWithSource('skip_next_segment', eventSource);
  }
  public playSkipPreviousSegment(eventSource: string): void {
    this.trackEventWithSource('skip_previous_segment', eventSource);
  }
  public playRestartSegment(eventSource: string): void {
    this.trackEventWithSource('skip_restart_segment', eventSource);
  }
  public playSegment(eventSource: string): void {
    this.trackEventWithSource('skip_play_segment', eventSource);
  }

  public showPlaylist(eventSource: string): void {
    this.trackEventWithSource('playlist_open', eventSource);
  }

  public pathTaken(
    eventSource: string,
    steeringMode: number,
    steeringReason: string
  ): void {
    this.trackEvent('path_taken', {
      event_source: eventSource,
      steering_mode: steeringMode,
      steering_reason: steeringReason,
    });
  }
  public steeringWheelShuffle(eventSource: string, steeringMode: number): void {
    this.trackEvent('steering_wheel_change', {
      event_source: eventSource,
      steering_mode: steeringMode,
    });
  }

  /*
  public signedIn(accessCode: string) {
    this.trackEvent(GaActionEnum.LOGIN, accessCode);
  }*/

  public mixStarted(source: string): void {
    this.trackEventWithSource('mix_play', source);
  }

  public contentSkipped() {
    this.trackEventWithSource('skipped', 'playlist');
  }

  public mixSaved() {
    this.trackEventWithSource('mix_saved', 'playlist');
  }

  public apiError(description: string, fatal = false) {
    this.trackEventWithSource('exception', 'system');
  }
}
